import { h, Component } from 'preact';
import { getCopy } from 'utils/copy';
import isShallowEqual from 'shallow-equals';
import Store from 'store/store';
import * as actions from 'store/actions';
import { sortObjectByOrder, accessNested, condClass, joinClasses, joinPaths, asList } from 'utils/utils';
import { marked } from 'marked';
import { Link } from 'components/core/link/link.js';
import Dropdown from 'components/shared/dropdown/dropdown.js';
import HTML from 'components/core/html/html.js';
import Social from 'components/shared/social/social.js';
import Follow from 'components/shared/follow/follow.js';
import TimedComponent from 'components/shared/timedComponent/timedComponent.js';
import BackgroundDrawer from 'components/shared/backgroundDrawer/backgroundDrawer';
import { generateBlurConvMatrix, applyConv } from 'utils/imageProcessing';
import { SvgSpeaker, SvgMuted } from 'components/shared/svgIcons/svgIcons';
import s from 'components/shared/header/header.sss';
import { smoothScrollTo } from 'utils/smoothScroll';

const isServer = typeof window === 'undefined';

export default class Header extends Component {

	constructor(...args) {
		super(...args);

		this.alternateLanguages = {
			ch: { language: 'DE', alternate: 'ch_fr' },
			ch_fr: { language: 'FR', alternate: 'ch' },
			be_nl: { language: 'NL', alternate: 'be_fr' },
			be_fr: { language: 'FR', alternate: 'be_nl' },
			ca: { language: 'EN', alternate: 'ca_fr' },
			ca_fr: { language: 'FR', alternate: 'ca' },
			nl: { language: 'NL', alternate: 'nl_en' },
			nl_en: { language: 'EN', alternate: 'nl' }
		};
		this.$items = {};
		this.topPos = 0;

		this.initFixedHeaderCalc = this.initFixedHeaderCalc.bind(this);
		this.updateClientStats = this.updateClientStats.bind(this);
		this.toggleMenu = this.toggleMenu.bind(this);
		this.handleToggleBtnEnter = this.handleToggleBtnEnter.bind(this);
		this.shouldHeaderBeFixed = this.shouldHeaderBeFixed.bind(this);
		this.renderNavItem = this.renderNavItem.bind(this);
		this.renderToggleButton = this.renderToggleButton.bind(this);
		this.safariTabBarFix = this.safariTabBarFix.bind(this);
	}

	componentDidMount() {
		if (this.props.fixHeaderTopOnScroll && !isServer) {
			this.initFixedHeaderCalc();
		}

		if (this.props.client.isSafari && this.props.client.device === 'ipad') {
			window.addEventListener('focus', this.safariTabBarFix);
		}
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (!isShallowEqual(nextState, this.state)) {
			return true;
		}
		// Update if we find a prop that's different from the previous version
		return !!Object.keys(nextProps).find(key => {
			if (key === 'showtimes') {
				// In the showtimes data we only care about the status
				// (maybe we should only pass that as a prop to the header)
				return nextProps[key].status !== this.props[key].status;
			}
			return nextProps[key] !== this.props[key];
		});
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.shouldHeaderBeFixed);
		window.removeEventListener('resize', this.updateClientStats);
		window.removeEventListener('focus', this.safariTabBarFix);
	}


	initFixedHeaderCalc() {
		this.topPos = this.$header.offsetTop;
		this.domBody = document.querySelector('body');

		window.addEventListener('scroll', this.shouldHeaderBeFixed);
		window.addEventListener('resize', this.updateClientStats);

		this.shouldHeaderBeFixed();
	}

	toggleMenu() {
		Store.emit(actions.SET_SIDE_NAV_OPEN, !this.props.headerData.sideNavOpen);
	}

	handleToggleBtnEnter(e) {
		if (e.keyCode === 13) {
			this.toggleMenu();
		}
	}

	safariTabBarFix() {
		// eslint-disable-next-line
		document.body.scrollTop = document.body.scrollTop;
	}

	shouldHeaderBeFixed() {
		const {
			forceFixHeader
		} = this.state;

		const {
			topPos,
			domBody
		} = this;

		const scrollPos = domBody.scrollTop;
		const atTop = topPos - scrollPos <= 0;
		if (atTop) {
			if (!forceFixHeader) {
				this.setState({
					forceFixHeader: true
				});
			}
		} else {
			if (forceFixHeader) {
				this.setState({
					forceFixHeader: false
				});
			}
		}
	}

	updateClientStats() {
		this.topPos = this.$header.offsetTop;
	}

	// Don't redraw the background when nothing changes
	shouldBackgroundDrawerRefresh() {
		const {
			$previousWidth,
			$previousHeight
		} = this;

		const {
			innerWidth: windowWidth,
			innerHeight: windowHeight
		} = window;

		const widthChanged = $previousWidth !== windowWidth;
		const heightChanged = $previousHeight !== windowHeight;

		// if the window has changed dimensions - store new dimensions
		if (widthChanged || heightChanged) {
			this.$previousWidth = windowWidth;
			this.$previousHeight = windowHeight;
			return true;
		}
		// Otherwise don't do anything
		return false;
	}

	// Should be bound to `this` to access the wrapper style
	bgDrawerPreprocessing(bg, size) {
		const style = getComputedStyle(this.base);
		const bgC = style && style.backgroundColor;
		const headerOpaque = !bgC || bgC.charAt(3) !== 'a';

		// The header is opaque, don't draw (not necessary)
		if (headerOpaque) return;

		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');

		// Reduce the size for faster computation when doing pixel-wise operations
		// (not visible as we blur anyway)
		canvas.width = ~~(size.width / 5);
		canvas.height = ~~(size.height / 5);

		// Use the full size if you disable the blur
		// canvas.width = size.width;
		// canvas.height = size.height;
		if (!canvas.width || !canvas.height) return;

		// Ensure the background is opaque
		const opaqueBgC = bgC.replace(/,[^,]+\)/, ',1)');
		ctx.fillStyle = opaqueBgC;
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);

		// > Blur, remove this and the try/catch to not use it (also make it full size, see a few lines up)
		let blurQuantity = accessNested(this.props.appData, 'header.backgroundBlur');
		if (blurQuantity === undefined || Number.isNaN(+blurQuantity)) blurQuantity = 7;
		const conv = generateBlurConvMatrix(+blurQuantity);
		// const conv = generateConvMatrix([
		// 	[-1, -2, -1],
		// 	[ 0, .1,  0],
		// 	[ 1,  2,  1]
		// ]);

		// Apply the convolution matrix
		try {
			const imd = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const out = ctx.createImageData(canvas.width, canvas.height);
			applyConv(conv, imd, out);
			ctx.putImageData(out, 0, 0);
		} catch (e) {
			// Can't use getImageData (canvas tainted because the source element is not allowing cross-origin requests)
			// Draw the unblurred image full size
			canvas.width = size.width; canvas.height = size.height;
			ctx.fillStyle = opaqueBgC;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);
		}

		ctx.fillStyle = bgC;
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		return canvas;
	}

	extraContentProps(extraContent, drawer) {
		let extraContentClass = extraContent.position && extraContent.position === 'right' ? s.extraContentImgRight : s.extraContentImgLeft;
		extraContentClass += condClass(extraContent.useRegionImg, s.regionalExtraContent);
		extraContentClass += condClass(this.props.headerData.sideNavActive && extraContent.hideOnMobile, s.hideExtraContent);

		if (drawer) {
			extraContentClass = s.extraContentCollapsed;
		}

		const extraContentStyle = {};
		if (extraContent.imgSrc) {
			extraContentStyle.backgroundImage = `url(${extraContent.imgSrc})`;
		}

		return {
			class: extraContentClass,
			style: extraContentStyle
		};
	}

	// RENDER METHODS
	//------------------------------------------
	renderFollow() {
		const props = this.props;
		const follow = accessNested(props, 'appData.header.follow');
		const { appData } = props;

		if (!follow) {
			return;
		}

		const {
			sideNavActive = false // (boolean) whether or not the mobile nav is being used
		} = props.headerData;

		const {
			meta,
			copy,
			styling
		} = props.appData;

		const {
			colorFollowIcons
		} = styling;

		const {
			$copy,
			items,
			shade
		} = follow;


		const title = $copy && copy[$copy];
		const titleElem = title && (
			<div class={s.followTitle}>
				{title}
			</div>
		);

		return (
			<div class={joinClasses(s.followContainer, sideNavActive && s.sideNavFollow)}>
				{titleElem}
				<Follow
					iconShade={shade}
					followClass={s.follow}
					followData={items}
					socialData={meta.social}
					iconShade={accessNested(appData, 'styling.socialIconShade', 'light')}
					copy={copy}
					color={colorFollowIcons}
				/>
			</div>
		);
	}

	extraDrawerContent() {
		const {
			appData,
			headerData
		} = this.props;

		const {
			itemClass,
			extraContentTrackId
		} = this;

		const extraContent = appData.header.extraContent;
		const sideNavActive = headerData.sideNavActive;

		if (!extraContent || extraContent.hideOnMobile || !sideNavActive) return;

		const extraContentProps = this.extraContentProps(extraContent, true);

		if (extraContent.url) {
			return (
				<div class={itemClass}>
					<Link
						href={extraContent.url}
						target="_blank"
						trackId={extraContentTrackId}
						{...extraContentProps}
					/>
				</div>
			);
		}
		return <div {...extraContentProps} />;
	}

	extraContent() {
		const appHeader = this.props.appData.header;
		const extraContent = appHeader.extraContent;
		const { sideNavActive } = this.props.headerData;

		if (!extraContent || sideNavActive) return;

		const extraContentProps = this.extraContentProps(extraContent, false);

		if (extraContent.url) {
			return (
				<Link
					href={extraContent.url}
					target="_blank"
					trackId={extraContent.trackId}
					{...extraContentProps}
				/>
			);
		}

		return <div {...extraContentProps} />;
	}

	getContentOutside() {}

	setToScroll(sectionTop, settings, parentScrollPage) {
		this.setState({
			navCurrent: sectionTop
		});
		this.toggleMenu();

		if (parentScrollPage === 'scrollSite') {
			setTimeout(() => smoothScrollTo(document.getElementById('showtimesApp'), document.getElementById(sectionTop), 750, settings));
		} else {
			setTimeout(() => smoothScrollTo('root', document.getElementById(sectionTop), 750, settings));
		}

	}

	renderToggleButton() {
		const sideNavOpen = this.props.headerData.sideNavOpen;
		const { activePage } = this.props;
		const homeEntStandardLayout = activePage.id === 'homeEnt' && !activePage.data.buttonLayout;

		return (
			<div
				class={s.toggleBtn + condClass(sideNavOpen, s.open) + condClass(!sideNavOpen, s.closed) + condClass(homeEntStandardLayout, s.standard)}
				role="button"
				onClick={this.toggleMenu}
				onKeyDown={this.handleToggleBtnEnter}
				tabIndex="0"
			>
				<div class={s.toggleBurger}>
					<div class={s.toggleBurgerLine} />
					<div class={s.toggleBurgerLine} />
					<div class={s.toggleBurgerLine} />
				</div>
			</div>
		);
	}

	renderAlternateLanguage() {
		const props = this.props;
		const { appData, dynamicData } = props;
		const currentRegion = accessNested(appData, 'meta.region.slug', '');
		let data = this.alternateLanguages[currentRegion];
		if (!data || !data.alternate) return;

		let neededAlternates = Array.isArray(data.alternate) ? data.alternate : [data.alternate];
		let altLinks = neededAlternates.map(alt => {
			let alternate = this.alternateLanguages[alt];
			let alternateUrl = accessNested(dynamicData, ['regions', alt, 'url']);
			if (!alternate || !alternateUrl) {
				return;
			}
			if (alternateUrl && !alternateUrl.match(/(\/|\.[0-9a-z]+)$/i)) {
				// No trailing slash and not a specific page (e.g. .html), so add the trailing slash (fixes a bug on studio urls)
				alternateUrl += '/';
			}
			if (alternate.redirectOff && alternateUrl.indexOf('redirect=off') === -1) {
				alternateUrl += (alternateUrl.indexOf('?') === -1 ? '?' : '&') + 'redirect=off';
			}
			return (
				<Link
					key={alternate.language}
					class={s.alternate}
					href={alternateUrl}
					trackId="alternate-language"
					trackLabel={alternate.language}
					renderCustomChild
				>
					{alternate.language}
				</Link>
			);
		}).filter(e => e);
		if (!altLinks.length) {
			return;
		}

		return (
			<div class={s.languageSwitcherWrapper} key="switcher">
				<div class={s.cta}>{accessNested(appData, 'copy.$CHOOSE_LANGUAGE', '')}</div>
				<div class={s.languageSwitcher} tabIndex="0">
					<div class={s.current} key="current">{data.language}</div>
					{altLinks}
				</div>
			</div>
		);
	}

	renderRegions() {
		const props = this.props;
		const { appData, dynamicData } = props;

		const useRegionSwitcher = accessNested(appData, 'options.useRegionSwitcher');
		const currentRegion = accessNested(appData, 'meta.region.slug', '');
		const currentFlag = accessNested(appData, 'header.currentFlag') || currentRegion;
		const currentLink = accessNested(appData, 'meta.url', '');
		const disabledRegions = asList(accessNested(appData, 'header.disabledFlags'));
		const allowedRegions = asList(accessNested(appData, 'header.allowedFlags'));
		const extraFlags = accessNested(appData, 'header.extraFlags', {});
		const regions = accessNested(dynamicData, 'regions', {});
		const otherRegionsList = Object.keys(regions).filter(key => {
			if (!regions[key]) return false;
			if (key === currentRegion) return false;
			let disabled = disabledRegions.indexOf(key) !== -1;
			let allowed = !allowedRegions.length || allowedRegions.indexOf(key) !== -1;
			return !disabled && allowed;
		});

		Object.keys(extraFlags).forEach(key => {
			const link = extraFlags[key];
			if (!link || key === currentRegion || link === currentLink) return;

			const alreadyThere = otherRegionsList.find(otherRegion => {
				return otherRegion === key || otherRegion.region === key;
			});
			if (alreadyThere) return;

			otherRegionsList.push({ region: key, link: link });
		});

		if (!useRegionSwitcher || !otherRegionsList.length) {
			return !!accessNested(appData, 'options.useAlternateLanguages') && this.renderAlternateLanguage();
		}

		otherRegionsList.sort((a, b) => {
			a = a.region || a; b = b.region || b;
			return a.localeCompare(b);
		});

		const flagRoot = joinPaths(accessNested(appData, 'urls.roots.sharedImages', ''), '/flags/');
		const flag = ({ region, link }) => {
			let elem = <div title={region} class={s.flag} id={region} style={{ backgroundImage: 'url(' + flagRoot + region + '.png)' }} />;
			if (link) {
				elem = <Link linkClass={s.flagLink} href={link} trackId={'region-switcher-' + currentRegion + '-' + region}>{elem}</Link>;
			}
			return elem;
		};

		const open = !!this.state.regionsOpen;
		let onClick;
		if (props.client.device !== 'desktop') {
			onClick = () => this.setState({ regionsOpen: !open });
		}

		const selectLanguageCTA = getCopy('$CHANGE_COUNTRY', appData.copy);

		return (
			<div class={joinClasses(s.regionsList, open && s.open)} onClick={onClick}>
				<div class={s.current}>
					{flag({ region: currentFlag })}
					<span class={s.select}>
						{selectLanguageCTA}
					</span>
				</div>
				<div class={s.other}>
					{otherRegionsList.map(otherRegion => {
						if (typeof otherRegion === 'string') {
							otherRegion = {
								region: otherRegion,
								link: regions[otherRegion].url
							};
						}
						return flag(otherRegion);
					})}
				</div>
			</div>
		);
	}

	// TODO: component HeaderItem ?
	renderNavItem(navItem, navId) {
		if (!navItem) return;

		const props = this.props;
		if (navItem.disableBeforeShowtimes && !props.showtimes.status) return;
		if (navItem.disableAfterShowtimes && props.showtimes.status) return;
		const { appData, activePage, client } = props;
		const pages = appData.pages;
		const copy = appData.copy;
		const sideNavActive = props.headerData.sideNavActive;

		const iconStyle = {};

		const pageId = navItem.id;

		let iconClass = '';
		if (navItem.iconClass) {
			iconClass = navItem.iconClass.charAt(0).toUpperCase() + navItem.iconClass.slice(1);
		}

		if (navItem.iconUrl) {
			iconStyle.backgroundImage = `url(${navItem.iconUrl})`;
		}

		let active;

		if (navItem.type === 'scrollTo') {
			// Treat active states of scrollTo navs so that..
			if (activePage.id === navItem.parentPage) {
				// scrollTo navs are only active on their parentPage, not on other pages
				if (!this.state.navCurrent) {
					// If their is no active nav item, set the active one to the scrollTo that matches the parent page id
					active = (navItem.id || navId) === navItem.parentPage;
				} else {
					// If the scrollTo is set to active in the state on click, make it active
					active = ((navItem.id || navId) === this.state.navCurrent);
				}
			}
		} else {
			// Treat other pages as normal where the active page should be active
			active = pageId === activePage.id;
		}

		// Ideally we should switch to .item.active.toggle (or any combination)
		// But not using itemToggleActive etc. would break a lot of overrides
		let itemClass = s['item' + (sideNavActive ? 'Toggle' : '') + (active ? 'Active' : '')] || '';
		itemClass += condClass(navItem.iconClass, s.icons);
		itemClass += condClass(navItem.extraClass, s[navItem.extraClass]);

		let comp = null;
		const ref = $ref => this.$items[navId] = $ref;

		let htmlId = 'header-' + (navItem.id || navId);

		switch (navItem.type) {
			case 'revealer':
			case 'social':
				const social = appData.meta.social;
				let socialClass = sideNavActive ? s.itemSocialToggle : itemClass;
				if (copy.$HASHTAG) {
					socialClass = sideNavActive ? s.itemSocialToggle : s.itemSocialHashtag;
				}
				comp = (
					<Social
						key={navId}
						appData={appData}
						items={navItem.items}
						social={social}
						appUrl={appData.meta.url}
						activePage={activePage}
						sideNav={sideNavActive}
						iconClass={iconClass}
						socialClass={socialClass}
						iconStyle={navItem.iconUrl && iconStyle}
						noRevealer={navItem.type !== 'revealer'}
						ref={ref}
					/>
				);
				break;
			case 'dropdown':
				const active = navItem.pageId === activePage.id;
				comp = (
					<Dropdown
						key={navId}
						pages={pages}
						appData={appData}
						title={navItem.$copy}
						childrenObj={navItem.dropdowns}
						pageId={navItem.pageId}
						wrapperClass={s.dropdown + condClass(active, s.active)}
						sideNavActive={sideNavActive}
						dropdownClass={itemClass}
						revealOnHover={!sideNavActive && (client.device === 'desktop' || !navItem.pageId)}
						useAbsolute={!sideNavActive}
						activePage={activePage}
						iconStyle={navItem.iconUrl && iconStyle}
						ref={ref}
					/>
				);
				break;
			case 'link':
				const extraOnClick = [];
				if (navItem.storeAction) {
					extraOnClick.push(e => Store.emit(navItem.storeAction, e));
				}
				comp = (
					<Link
						key={navId}
						trackId={navItem.trackId}
						childId={navItem.id} // ID added for third party tracking purposes
						href={navItem.link}
						exitModal={navItem.exitModal}
						id={htmlId}
						target={navItem.target}
						linkClass={itemClass}
						iconClass={iconClass}
						iconStyle={navItem.iconUrl && iconStyle}
						ref={ref}
						extraOnClick={extraOnClick}
						childHTML={marked(copy[navItem.$copy] || '')}
					/>
				);
				break;
			case 'text':
				let content = copy[navItem.$copy] || navItem.$copy || '';
				comp = (
					<HTML
						id={htmlId}
						content={content}
						markdown
						ref={ref}
					/>
				);
				break;
			case 'scrollTo':
				const parentScrollPage = navItem.parentPage;
				const section = copy[navItem.$copy] || copy[pages[pageId].$copy];
				const sectionTop = (navItem.id || navId);
				const scrollSettings = navItem.scrollSettings || 0;
				const navItemId = navItem.parentPage === 'scrollSite' ? 'scroll-' + navItem.id : navItem.id;
				comp = (
					<Link
						key={navId}
						childId={navItemId}
						pageId={parentScrollPage}
						scrollSection={sectionTop}
						id={htmlId}
						trackId={navItem.trackId}
						linkClass={itemClass}
						iconClass={iconClass}
						iconStyle={navItem.iconUrl && iconStyle}
						ref={ref}
						extraOnClick={() => this.setToScroll(sectionTop, scrollSettings, parentScrollPage)}
						ariaLabel={navItem.ariaLabel}
						title={navItem.title}
					>
						{section}
					</Link>
				);
				break;
			default:
				if (pages[pageId]) {
					const label = copy[navItem.$copy] || copy[pages[pageId].$copy];
					const extraOnClick = [];
					if (navItem.storeAction) {
						extraOnClick.push(e => Store.emit(navItem.storeAction, e));
					}
					comp = (
						<Link
							key={navId}
							childId={navItem.id} // ID added for third party tracking purposes
							pageId={pageId}
							id={htmlId}
							trackId={navItem.trackId}
							linkClass={itemClass}
							iconClass={iconClass}
							iconStyle={navItem.iconUrl && iconStyle}
							extraOnClick={extraOnClick}
							ref={ref}
						>
							{label}
						</Link>
					);
				}
		}
		const timer = navItem.timed;
		if (comp && timer) {
			comp = (
				<TimedComponent
					key={navItem.id}
					startDate={timer.startDate}
					startTime={timer.startTime}
					endDate={timer.endDate}
					endTime={timer.endTime}
					timezone={timer.endTime}
				>
					{comp}
				</TimedComponent>
			);
		}
		return comp;
	}

	renderNavItems() {
		return sortObjectByOrder(this.props.appData.header.nav, this.renderNavItem);
	}

	render(props) {
		const {
			appData,
			background,
			headerData,
			activePage,
			iosFixedFix,
			client
		} = props;

		const {
			sideNavActive,
			sideNavOpen,
			hideHeader
		} = headerData;

		const appHeader = appData.header || {};
		const nav = appHeader.nav;
		if (!nav) return;

		let toggleBtn;
		let headerClass = s.wrapper;
		let navClass = joinClasses(s.nav, appHeader.dynamicItemWidth && s.dynamicItemWidth);
		let closeBackClass = s.closeBack;

		const activePageData = activePage.data || {};

		let topImage;

		if (sideNavActive) {
			const seeTheaters = accessNested(props, 'activePage.data.seeTheaters');
			const url = accessNested(props, 'activePage.data.seeTheaters.url');

			headerClass = s.wrapperToggle;
			navClass = s.navToggle;

			if (sideNavOpen) {
				navClass = s.navToggleOpen;
				closeBackClass += condClass(s.visible);
			}

			if (seeTheaters && url) {
				headerClass = joinClasses(s.wrapperToggle, s.headerHeight);
			}

			toggleBtn = this.renderToggleButton();
		} else {
			if (appHeader.topImage) {
				const style = { backgroundImage: 'url(' + appHeader.topImage + ')' };
				if (appHeader.topImageColor) style.backgroundColor = appHeader.topImageColor;
				topImage = <div class={s.topImage} style={style} />;
			}
		}

		const vidPlaying = background.videoEnabled && background.videoPlaying;
		const backgroundDrawerActive = activePageData.backgroundBehindHeader && !activePageData.opaqueHeader && !sideNavActive && !vidPlaying;

		headerClass += condClass(hideHeader || activePageData.hideHeader || headerData.hidden, s.hide);

		if (this.state.forceFixHeader) {
			headerClass = s.forceFixWrapper;
		}

		let muteButton;
		const useMuteButton = accessNested(appData, 'options.bgWithSound');
		if (useMuteButton) {
			const muted = background.muted !== false;
			const SvgIcon = muted ? SvgMuted : SvgSpeaker;
			muteButton = (
				<div class={joinClasses(s.muteButton, muted && s.muted)} onClick={() => Store.emit(actions.SET_BACKGROUND_VIDEO_MUTED, !muted)}>
					<SvgIcon class={s.muteIcon} key={muted ? 'muted' : 'speaker'} />
				</div>
			);
		}

		const wrapperStyle = {};

		if (iosFixedFix && !this.disableFixedFix) {
			wrapperStyle.position = 'absolute';
			wrapperStyle.marginTop = iosFixedFix + 'px';
		}

		let regionsElement = this.renderRegions();

		const openClose = sideNavOpen ? s.open : s.closed;
		const followOutsideHeader = accessNested(appData, 'header.follow.outsideHeader');
		const renderFollowOutside = client.deviceSize !== 'mobile' && sideNavActive && followOutsideHeader;

		// header-nav class added for sony tracking
		return (
			<header
				class={joinClasses(headerClass, activePageData.fullBackgroundHeader && s.fullBackground, activePageData.opaqueHeader && s.opaque, openClose)}
				ref={$ref => this.$header = $ref}
				style={wrapperStyle}
			>
				{toggleBtn}
				<div class={closeBackClass} onClick={this.toggleMenu} key="toggle-menu" />
				{topImage}
				<nav class={joinClasses(navClass, 'header-nav')} key="nav" ref={$ref => this.$nav = $ref}>
					{sideNavActive && regionsElement}
					{this.renderNavItems()}
					{!renderFollowOutside && this.renderFollow()}
					{this.extraDrawerContent()}
				</nav>
				<BackgroundDrawer
					class={joinClasses(s.background, backgroundDrawerActive && s.active)}
					background={background.element}
					activePage={activePage}
					continuous
					active={backgroundDrawerActive}
					shouldRefresh={this.shouldBackgroundDrawerRefresh}
					preprocessing={this.bgDrawerPreprocessing.bind(this)}
					key="background-drawer"
				/>
				{!sideNavActive && regionsElement}
				{muteButton}
				{this.extraContent()}
				<div class={s.contentOutside} key="content-outside">{this.getContentOutside()}</div>
				{renderFollowOutside && <div class={s.outsideFollow}>{this.renderFollow()}</div>}
			</header>
		);
	}

}
